/**
 * NSM (North Star Metric) tile — Weekly New Match-Ready.
 *
 * Backend shape: { current_week: number, prior_week: number, wow_pct: number|null,
 *                  spark_7d: Array<{ d: string, n: string }> }
 * (see backend/src/routes/admin-analytics-dashboard.ts::buildNsmTile)
 */
import { formatNumber } from '@/lib/utils';

interface NsmData {
  current_week: number;
  prior_week: number;
  wow_pct: number | null;
  spark_7d: Array<{ d: string; n: string | number }>;
}

export function NsmTile({ data }: { data: NsmData }) {
  const arrow = data.wow_pct == null ? '' : data.wow_pct >= 0 ? '↗' : '↘';
  const wowColor =
    data.wow_pct == null ? 'text-muted-foreground' : data.wow_pct >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="bg-card border rounded-lg p-6 md:col-span-2 lg:col-span-2">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        Weekly New Match-Ready (NSM)
      </div>
      <div className="flex items-baseline gap-3 mt-2">
        <div className="text-4xl font-bold">{formatNumber(data.current_week)}</div>
        {data.wow_pct != null && (
          <div className={`text-sm font-medium ${wowColor}`}>
            {arrow} {Math.abs(data.wow_pct).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        vs prior week: {formatNumber(data.prior_week)}
      </div>
      <Sparkline points={data.spark_7d} />
    </div>
  );
}

function Sparkline({ points }: { points: Array<{ d: string; n: string | number }> }) {
  if (!points || points.length === 0) {
    return (
      <div className="text-xs text-muted-foreground mt-3">No data points yet for the last 7 days.</div>
    );
  }
  const nums = points.map((p) => (typeof p.n === 'number' ? p.n : parseInt(p.n, 10) || 0));
  const max = Math.max(...nums, 1);
  const width = 200;
  const height = 40;
  const barWidth = width / nums.length;
  return (
    <svg width={width} height={height} className="mt-3" aria-label="7-day sparkline">
      {nums.map((n, i) => {
        const h = (n / max) * height;
        return (
          <rect
            key={i}
            x={i * barWidth + 1}
            y={height - h}
            width={Math.max(barWidth - 2, 1)}
            height={h}
            className="fill-primary"
          />
        );
      })}
    </svg>
  );
}
